import { message } from "antd";
import { authApiSlice } from "@/services/api/auth/authApi";
import { clearStorage } from "@/utils/storageHelpers";
import { createSlice } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

const initialState = {
  loginStatus: "idle",
  loginError: null,
  authUser: null,
  token: undefined,
};

// Helper to extract error message safely
const getErrorMessage = (action) => {
  return action.payload?.data?.message || "An unexpected error occurred. Please try again.";
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      Object.assign(state, initialState);
      clearStorage("lastUpdated");
      clearStorage("timeLeft");
    },
    updateTokens: (state, action) => {
      state.token = action.payload.token;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login - Pending
      .addMatcher(authApiSlice.endpoints.loginApi.matchPending, (state) => {
        state.loginStatus = "loading";
        state.loginError = null;
      })
      // Login - Fulfilled
      .addMatcher(
        authApiSlice.endpoints.loginApi.matchFulfilled,
        (state, action) => {
          const payload = action.payload?.data;
          const token = action.payload?.token;
          if (token) {

            state.loginStatus = "succeeded";
            state.loginError = null;
            state.authUser = payload;
            state.token = token;
            message.success("Login successful");
          } else {

            state.loginStatus = "failed";
            state.loginError = "Invalid login details";
            state.authUser = null;
            state.token = undefined;
            message.error("Invalid login details");
          }
        }
      )
      // Login - Rejected
      .addMatcher(
        authApiSlice.endpoints.loginApi.matchRejected,
        (state, action) => {
          state.loginStatus = "failed";

          if (!navigator.onLine) {
            const offlineMsg = "You are offline. Check your network connection";
            state.loginError = [offlineMsg];
            message.error(offlineMsg);
            return;
          }

          const errorMsg = getErrorMessage(action);
          state.loginError = errorMsg;

          // Handle specific error cases
          if (errorMsg === "User must reset password to access the system.") {
            message.info(errorMsg);
          } else {
            message.error(errorMsg);
          }
        }
      )
      // Reset Password Request - Fulfilled
      .addMatcher(
        authApiSlice.endpoints.resetPasswordRequestApi.matchFulfilled,
        () => {
          message.success("Password reset link sent to your email!");
        }
      )
      // Reset Password Request - Rejected
      .addMatcher(
        authApiSlice.endpoints.resetPasswordRequestApi.matchRejected,
        (state, action) => {
          if (!navigator.onLine) {
            message.error("You are offline. Check your network connection");
            return;
          }
          const errorMsg = getErrorMessage(action);
          message.error(errorMsg);
        }
      );
  },
});

const authPersistConfig = {
  key: "auth",
  storage,
  whitelist: ["token", "authUser"],
};

export const { logout, updateTokens } = authSlice.actions;

export const selectAuthState = {
  loginStatus: (state) => state.auth.loginStatus,
  loginError: (state) => state.auth.loginError,
  authUser: (state) => state.auth.authUser,
  token: (state) => state.auth.token,
};

export default persistReducer(authPersistConfig, authSlice.reducer);